import { awaitMswReady, shouldLogMsw } from '@/mocks/runtime';
import type { ApiResponseT } from '@/types/common';
import { getAccessTokenCookieName } from '@/utils/authToken';
import {
  getClientSideUserAgent,
  getCookie,
  isClientSide,
  isNonNil,
  isServerSide,
} from '@vuddy/utils';
import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios';
import { env } from 'next-runtime-env';
import { API_URL, MEDIA_API_URL } from './apiConfig';
import { logApiError } from './logApiError';
import webViewService from './webViewService';

const getAuthorizationToken = (accessToken: string) => `Bearer ${accessToken}`;

const createErrorHandler = () => (error: AxiosError) => {
  logApiError(error);
  return Promise.reject(error);
};

const PHASE = env('NEXT_PUBLIC_PHASE');

const requestInstance = axios.create({
  baseURL: isServerSide ? `${API_URL}/api/v1` : '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

const requestInstanceV2 = axios.create({
  baseURL: isServerSide ? `${API_URL}/api/v2` : '/api/v2',
  headers: {
    'Content-Type': 'application/json',
  },
});

const requestInstanceV3 = axios.create({
  baseURL: isServerSide ? `${API_URL}/api/v3` : '/api/v3',
  headers: {
    'Content-Type': 'application/json',
  },
});

// --- Next.js API Routes 전용 Axios 인스턴스  ---
const nextApiRequestInstance = axios.create({
  baseURL: '/',
  headers: {
    'Content-Type': 'application/json',
  },
});

const mediaRequestInstance = axios.create({
  baseURL: `${MEDIA_API_URL}/api/v1/resources`,
  headers: {
    'x-auth-service-name': PHASE === 'sandbox' ? 'vuddy_sandbox' : 'vuddy',
    'Content-Type': 'multipart/form-data',
  },
});

export const setAuthorizationHeader = (token: string) => {
  requestInstance.defaults.headers['Authorization'] =
    getAuthorizationToken(token);
  requestInstanceV2.defaults.headers['Authorization'] =
    getAuthorizationToken(token);
  requestInstanceV3.defaults.headers['Authorization'] =
    getAuthorizationToken(token);
};

export const removeAuthorizationHeader = () => {
  delete requestInstance.defaults.headers['Authorization'];
  delete requestInstanceV2.defaults.headers['Authorization'];
  delete requestInstanceV3.defaults.headers['Authorization'];
};

export const setXDevicePlatform = (platform: string) => {
  requestInstance.defaults.headers['X-DEVICE-PLATFORM'] = platform;
  requestInstanceV2.defaults.headers['X-DEVICE-PLATFORM'] = platform;
  requestInstanceV3.defaults.headers['X-DEVICE-PLATFORM'] = platform;
};

const ensureServerMswReady = async () => {
  if (!isServerSide) {
    return;
  }

  const { ensureServerMsw } = await import('@/mocks/server');
  if (shouldLogMsw()) {
    console.info('[MSW][request] ensuring server MSW');
  }
  await ensureServerMsw();
};

const createRequestInterceptor =
  (options: { waitForMsw?: boolean } = {}) =>
  async (config: InternalAxiosRequestConfig) => {
    if (options.waitForMsw) {
      if (isClientSide) {
        await awaitMswReady();
      } else {
        await ensureServerMswReady();
      }
    }

    if (!(config.headers instanceof AxiosHeaders)) {
      config.headers = new AxiosHeaders(config.headers);
    }

    const headers = config.headers as AxiosHeaders;
    const accessToken = getCookie(getAccessTokenCookieName());
    if (accessToken) {
      headers.set('Authorization', getAuthorizationToken(accessToken));
    }

    if (isClientSide) {
      const clientSideXDevicePlatform = webViewService.isAppWebView(
        getClientSideUserAgent()
      )
        ? webViewService.getDevicePlatform(getClientSideUserAgent())
        : 'WEB';
      headers.set('X-DEVICE-PLATFORM', clientSideXDevicePlatform);
      headers.set('Accept-Language', 'ko-KR');
    } else if (isServerSide) {
      headers.set('Accept-Language', 'ko-KR');
    }

    return config;
  };

requestInstance.interceptors.request.use(
  createRequestInterceptor({ waitForMsw: true })
);
requestInstanceV2.interceptors.request.use(
  createRequestInterceptor({ waitForMsw: true })
);
requestInstanceV3.interceptors.request.use(
  createRequestInterceptor({ waitForMsw: true })
);
mediaRequestInstance.interceptors.request.use(createRequestInterceptor());
nextApiRequestInstance.interceptors.request.use(createRequestInterceptor());

mediaRequestInstance.interceptors.response.use(
  res => res.data,
  createErrorHandler()
);

[requestInstance, requestInstanceV2, requestInstanceV3].forEach(instance => {
  instance.interceptors.response.use(res => {
    const { data } = res as unknown as AxiosResponse<ApiResponseT<any>>;

    if (isNonNil(data.error)) {
      return Promise.reject(data.error);
    } else {
      return data.data;
    }
  }, createErrorHandler());
});

nextApiRequestInstance.interceptors.response.use(
  res => res.data,
  createErrorHandler()
);

export const request = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstance.get(url, config),
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstance.post(url, data, config),
  patch: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => requestInstance.patch(url, data, config),
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstance.put(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstance.delete(url, config),
};

export const requestV2 = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV2.get(url, config),
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV2.post(url, data, config),
  patch: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => requestInstanceV2.patch(url, data, config),
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV2.put(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV2.delete(url, config),
};

export const requestV3 = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV3.get(url, config),
  post: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV3.post(url, data, config),
  patch: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => requestInstanceV3.patch(url, data, config),
  put: <T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV3.put(url, data, config),
  delete: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    requestInstanceV3.delete(url, config),
};

export const mediaRequest = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    mediaRequestInstance.get(url, config),
  post: <T>(url: string, data: any, config?: AxiosRequestConfig): Promise<T> =>
    mediaRequestInstance.post(url, data, config),
};

export const nextApiRequest = {
  get: <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    nextApiRequestInstance.get(url, config),
  post: <T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig
  ): Promise<T> => {
    return nextApiRequestInstance.post(url, data, config);
  },
};
